import { useEffect, useState } from 'react'

/** Роль — по данным /api/auth/me (Keycloak realm utm) */
export type IvoRole = 'dispatcher' | 'pilot' | 'auditor' | 'admin'

/* ============================================================================
 * ПРАВА ПО ЭКРАНАМ И ВКЛАДКАМ
 *
 * Задача №13. Разведка показала: роли на бэкенде разведены по-настоящему
 * (ivo_auth.py RULES), а интерфейс показывал ВСЕ панели каждому безусловно.
 * Итог: аудитору показывали вкладку «БВС» с управлением дронами (ему 403),
 * пилоту — реестр геозон (тоже 403). Роль была написана в шапке и больше
 * нигде не учитывалась.
 *
 * Права сверены с фактическими ответами бэкенда (проверено 2026-10-04):
 *   маршрут                 диспетчер  пилот  аудитор
 *   GET /api/mavlink            200      403      403
 *   GET /api/declarations        200      200      403
 *   GET /api/geo                200      403      403
 *   GET /api/traffic            200      200      200
 *   GET /api/trust              200      200      200
 *   GET /api/incident/…/bundle   200      403      200
 * При смене правил в ivo_auth.RULES правьте ТОЛЬКО эту таблицу — держим её
 * рядом с комментарием, чтобы расхождение «роль ↔ права» было заметно.
 * ========================================================================= */

/** Что видит роль на консоли. Порядок = порядок переключения на мобильном. */
export type Screen = 'map' | 'pilot' | 'situational' | 'auditor'

export const ROLE_LABEL: Record<IvoRole, string> = {
  dispatcher: 'Диспетчер',
  pilot: 'Пилот',
  auditor: 'Аудитор',
  admin: 'Администратор',
}

/** Названия экранов — для переключателя на телефоне */
export const SCREEN_LABEL: Record<Screen, string> = {
  map: 'Карта',
  pilot: 'Пилот',
  situational: 'Ситуационный',
  auditor: 'Аудитор',
}

/** Стартовый экран по роли: диспетчеру — карта, остальным — свой */
export const ROLE_HOME: Record<IvoRole, Screen> = {
  dispatcher: 'map',
  pilot: 'pilot',
  auditor: 'auditor',
  admin: 'map',
}

/**
 * Вкладки панели пилота по роли.
 * 'mav' (управление БВС) и согласование заявок закрыты для пилота и аудитора:
 * /api/mavcmd и PUT /api/declarations — только ЦУП.
 */
export const PILOT_TABS_BY_ROLE: Record<IvoRole, PilotTabId[]> = {
  dispatcher: ['sppi', 'remoteid', 'mav', 'declarations', 'profile'],
  pilot: ['sppi', 'remoteid', 'declarations', 'profile'],
  auditor: ['profile', 'declarations_read'],
  admin: ['sppi', 'remoteid', 'mav', 'declarations', 'profile'],
}

/** Вкладки ситуационного центра по роли.
 *  'l3' (2026-10-07i) — тренажёр №1 UTM-диспетчер на twin: только ЦУП
 *  (инструктор v1); ручки /api/l3/* закрыты RULES пустым кортежем. */
export const SIT_TABS_BY_ROLE: Record<IvoRole, SitTabId[]> = {
  dispatcher: ['tracks', 'range', 'trust', 'l3', 'weather', 'geofences', 'nebosvod', 'manage'],
  pilot: ['tracks', 'range', 'l3', 'weather'],
  auditor: ['tracks', 'trust', 'weather', 'auditor'],
  admin: ['tracks', 'range', 'trust', 'l3', 'weather', 'geofences', 'nebosvod', 'manage', 'auditor'],
}

export type PilotTabId =
  | 'sppi'
  | 'remoteid'
  | 'mav'
  | 'declarations'
  | 'profile'
  | 'declarations_read'

export type SitTabId =
  | 'tracks'
  | 'range'
  | 'trust'
  | 'l3'
  | 'weather'
  | 'geofences'
  | 'nebosvod'
  | 'manage'
  | 'auditor'

/** Роль из массива ролей: берём первую известную (обычно она одна) */
export function roleOf(roles: Array<IvoRole | string> | undefined): IvoRole | null {
  if (!roles || roles.length === 0) return null
  for (const r of ['dispatcher', 'pilot', 'auditor', 'admin'] as IvoRole[]) {
    if (roles.includes(r)) return r
  }
  return null
}

/* ============================================================================
 * МОБИЛЬНЫЙ РЕЖИМ — ОДИН ЭКРАН
 *
 * Замер на 375×667 показал: сетка из трёх колонок на телефоне даёт контейнер
 * карты высотой 0 (canvas 357×300 внутри 357×0), страница не прокручивается,
 * 25 кнопок меньше 40px и кнопка «Выйти» размером 45×18. Всё, что ниже
 * первого экрана, физически недостижимо для пальца.
 *
 * Поэтому ниже 768px показываем ОДНУ панель за раз и даём переключатель
 * «Карта / Пилот / Ситуационный / Аудитор». Карта на телефоне получает
 * всю высоту экрана — это единственный способ показать её вообще.
 * ========================================================================= */

/** Ширина, ниже которой включается мобильный режим, px */
export const MOBILE_BREAKPOINT = 768

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < MOBILE_BREAKPOINT,
  )
  useEffect(() => {
    // resize, а не matchMedia: смена ширины при повороте телефона — тот же
    // случай, что и уменьшение окна, и один обработчик покрывает оба.
    const onResize = () => setMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    onResize() // на старте ширина может отличаться (например, после поворота)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return mobile
}